/**
 * Client-seitige Live-Stufen für ARKO-Wartezeit (MVP, spiegelt n8n-Pipeline).
 * Echte Schritte kommen nach Antwort via reasoning_trace.pipeline.
 */

import { extractFirstUrl } from './arkoWebArticle';
import { isExplicitPortalFristQuestion } from './portalDocumentFristProbe';

export type ThinkingFlow =
  | 'webhook'
  | 'web_article_fetch'
  | 'document_analyze'
  | 'contract'
  | 'synthesis_apply';

export interface ThinkingContext {
  flow: ThinkingFlow;
  chatInput: string;
  intent?: string;
  hasDocumentContext?: boolean;
  hasWebArticleMeta?: boolean;
  skipProactiveFrist?: boolean;
}

export interface ThinkingStage {
  id: string;
  label: string;
}

const LEGAL_SECTION_RE = /(?:§{1,2}\s*\d+|\bparagraph(?:en)?\s+\d+)/i;
const LEGAL_LAW_RE =
  /\b(ABGB|MRG|StGB|StPO|UrhG|UStG|EStG|KStG|GmbHG|AktG|BAO|VStG|GewO|StVO|KSchG|EheG|EO|UGB|HGB|FBG|RAO|VVG|GBG|AVG|WGG|MaklerG|JN|ZPO|UWG|DSG|DSGVO)\b/i;

export function looksLikeLegalQuestionText(text: string): boolean {
  return LEGAL_SECTION_RE.test(text) || LEGAL_LAW_RE.test(text);
}

export function detectThinkingInputSignals(text: string, intent?: string) {
  const trimmed = String(text || '').trim();
  const normalizedIntent = String(intent || '').trim().toLowerCase();
  return {
    hasUrl: Boolean(extractFirstUrl(trimmed)),
    isLegal:
      normalizedIntent === 'rechtsfrage' ||
      looksLikeLegalQuestionText(trimmed),
    isFrist:
      normalizedIntent === 'frist_vorschlag' ||
      isExplicitPortalFristQuestion(trimmed),
    isSchreiben: normalizedIntent === 'schreiben_erstellen',
    isVertrag: normalizedIntent === 'vertrag_erstellen',
    isSummarize: normalizedIntent === 'zusammenfassen',
  };
}

function webhookStages(ctx: ThinkingContext): ThinkingStage[] {
  const signals = detectThinkingInputSignals(ctx.chatInput, ctx.intent);
  const stages: ThinkingStage[] = [
    { id: 'normalize', label: 'Anfrage wird normalisiert…' },
  ];

  if (ctx.hasDocumentContext || ctx.hasWebArticleMeta || signals.hasUrl) {
    stages.push({
      id: 'document_context',
      label: ctx.hasWebArticleMeta || signals.hasUrl
        ? 'Web-Artikel wird eingebunden…'
        : 'Dokumentkontext wird eingebunden…',
    });
  }

  stages.push({ id: 'intent_detect', label: 'Intent wird erkannt…' });

  const showFristProbe =
    signals.isFrist ||
    (ctx.hasDocumentContext && !ctx.skipProactiveFrist && !signals.isLegal);
  if (showFristProbe) {
    stages.push({ id: 'frist_probe', label: 'Frist-Sonde läuft…' });
  }

  if (signals.isLegal) {
    stages.push({ id: 'lexcore', label: 'LEXCORE wird geprüft…' });
    stages.push({ id: 'answer', label: 'Antwort wird formuliert…' });
  } else if (signals.isSchreiben) {
    stages.push({ id: 'schreiben_draft', label: 'Schreiben-Entwurf wird erstellt…' });
  } else if (signals.isVertrag) {
    stages.push({ id: 'vertrag_generator', label: 'Vertragsentwurf wird vorbereitet…' });
  } else {
    stages.push({ id: 'llm', label: 'Antwort wird erstellt (Ollama)…' });
  }

  return stages;
}

/** Reihenfolge der Live-Stufen passend zum erkannten Input / Flow. */
export function buildThinkingStages(ctx: ThinkingContext): ThinkingStage[] {
  switch (ctx.flow) {
    case 'web_article_fetch':
      return [{ id: 'web_fetch', label: 'Artikel wird geladen…' }];
    case 'document_analyze':
      return [
        { id: 'document_read', label: 'Dokumente werden gelesen…' },
        { id: 'normalize', label: 'Anfrage wird normalisiert…' },
        { id: 'intent_detect', label: 'Intent wird erkannt…' },
        { id: 'llm', label: 'Antwort wird erstellt (Ollama)…' },
      ];
    case 'contract':
      return [
        { id: 'contract_slots', label: 'Vertragsfelder werden ausgewertet…' },
        { id: 'vertrag_generator', label: 'Vertragsentwurf wird erstellt…' },
      ];
    case 'synthesis_apply':
      return [{ id: 'synthesis_apply', label: 'Dokument wird angelegt…' }];
    case 'webhook':
    default:
      return webhookStages(ctx);
  }
}

/** Rotiert den aktiven Schritt (1,5–2,5 s pro Stufe). */
export function pickActiveStageIndex(stageCount: number, tick: number): number {
  if (stageCount <= 0) return 0;
  const intervalMs = stageCount <= 3 ? 1800 : 2200;
  const idx = Math.floor(tick / intervalMs);
  return Math.min(idx, stageCount - 1);
}
